/* eslint-disable no-param-reassign */
import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';
import distributionApi from 'services/api/distribution';
import { type Release } from 'services/api/entities';
import {
  ReleaseList,
  ReleaseSong,
  ReleaseSongStatus,
} from 'services/api/entities/distribution/release';
import { NAMESPACE } from './constants';
import initialState from './initialState';
import { ReleaseFieldType, ReleaseSongFieldType } from './types';
import { getSongStatus, hasError } from './helpers/getReleaseSongStatus';

const createReleaseSongData = ({ metadata, ...baseSong }: ReleaseSong) => ({
  ...baseSong,
  metadata: {
    [ReleaseSongFieldType.ComposerName]: {
      loading: false,
      error: null,
      data: metadata?.composer ?? null,
    },
    [ReleaseSongFieldType.HasVocals]: {
      loading: false,
      error: null,
      data: metadata?.hasVocals ?? null,
    },
    [ReleaseSongFieldType.VocalsContent]: {
      loading: false,
      error: null,
      data: metadata?.vocalsContent ?? null,
    },
    [ReleaseSongFieldType.Language]: {
      loading: false,
      error: null,
      data: metadata?.language ?? null,
    },
    [ReleaseSongFieldType.VocalistName]: {
      loading: false,
      error: null,
      data: metadata?.vocalist ?? null,
    },
    [ReleaseSongFieldType.AdditionalArtists]: {
      loading: false,
      error: null,
      data: metadata?.additionalArtists ?? null,
    },
    [ReleaseSongFieldType.SongRemixers]: {
      loading: false,
      error: null,
      data: metadata?.remixers ?? null,
    },
    [ReleaseSongFieldType.SongProducers]: {
      loading: false,
      error: null,
      data: metadata?.producers ?? null,
    },
  },
});

const createReleaseState = (release: Release) => ({
  id: release.id,
  status: release.status,
  [ReleaseFieldType.SongTitle]: {
    loading: false,
    error: null,
    data: release.title,
  },
  [ReleaseFieldType.Genre]: {
    loading: false,
    error: null,
    data: release.genre,
  },
  [ReleaseFieldType.CoverArt]: {
    loading: false,
    error: null,
    data: release.coverArtPath,
  },
  [ReleaseFieldType.ArtistName]: {
    loading: false,
    error: null,
    data: release.artistName,
  },
  songs: release.songs.map(createReleaseSongData),
});

const distributionSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    setCoverArtLocally: (
      state,
      action: PayloadAction<{ releaseId: string; imgSrc: string | null }>
    ) => {
      const {
        payload: { releaseId, imgSrc },
      } = action;
      const release = state.releases[releaseId];
      if (!release) {
        return;
      }

      release[ReleaseFieldType.CoverArt].data = imgSrc;
    },
    setReleaseFieldError: (
      state,
      action: PayloadAction<{
        releaseId: string;
        field: ReleaseFieldType;
        error: SerializedError | null;
      }>
    ) => {
      const {
        payload: { releaseId, field, error },
      } = action;

      const release = state.releases[releaseId];
      if (!release) {
        return;
      }

      release[field].error = error;

      /* TODO: Remove this code in STR-3466
         Reason: This logic is temporary until song title and album get separated on the BE 
       */
      const currentSongData = release.songs[0];
      currentSongData.status = getSongStatus(release);
    },
    setReleaseSongFieldError: (
      state,
      action: PayloadAction<{
        releaseId: string;
        field: ReleaseSongFieldType;
        error: SerializedError | null;
      }>
    ) => {
      const {
        payload: { releaseId, field, error },
      } = action;

      const release = state.releases[releaseId];
      if (!release) {
        return;
      }

      const currentSongData = release.songs[0];
      const { metadata } = currentSongData;
      if (!metadata) {
        return;
      }

      metadata[field].error = error;
      currentSongData.status = getSongStatus(release);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      distributionApi.endpoints.getGenres.matchFulfilled,
      (state, action) => {
        const { payload: genres } = action;
        state.genres = genres;
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.createRelease.matchPending,
      (state, action) => {
        const {
          meta: {
            arg: {
              originalArgs: { songId },
            },
          },
        } = action;

        state.releaseRequests[songId] = {
          data: null,
          loading: true,
          error: null,
        };
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.createRelease.matchFulfilled,
      (state, action) => {
        const {
          payload: { release },
          meta: {
            arg: {
              originalArgs: { songId },
            },
          },
        } = action;

        const { releaseRequests, releases } = state;
        releaseRequests[songId] = {
          data: release.id,
          loading: false,
          error: null,
        };
        releases[release.id] = createReleaseState(release);
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.createRelease.matchRejected,
      (state, action) => {
        const {
          payload,
          meta: {
            arg: {
              originalArgs: { songId },
            },
          },
        } = action;

        state.releaseRequests[songId] = {
          data: null,
          loading: false,
          error: payload as SerializedError,
        };
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.getRelease.matchFulfilled,
      (
        state,
        {
          payload: release,
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { releaseId } = originalArgs;

        state.releases[releaseId] = createReleaseState(release);
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateRelease.matchPending,
      (
        state,
        {
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { releaseId, field } = originalArgs;
        const release = state.releases[releaseId];
        if (!release) {
          return;
        }

        release[field].loading = true;
        release[field].error = null;
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateRelease.matchRejected,
      (state, action) => {
        const {
          payload,
          meta: {
            arg: { originalArgs },
          },
        } = action;
        const { releaseId, field } = originalArgs;

        const release = state.releases[releaseId];
        if (!release || !payload) {
          return;
        }

        release[field].loading = false;
        release[field].error = payload as SerializedError;

        /* TODO: Remove this code in STR-3466
           Reason: This logic is temporary until song title and album get separated on the BE 
         */
        const currentSongData = release.songs[0];
        currentSongData.status = getSongStatus(release);
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateRelease.matchFulfilled,
      (
        state,
        {
          payload: updatedRelease,
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { releaseId, field } = originalArgs;
        const currentRelease = state.releases[releaseId];
        if (!currentRelease) {
          return;
        }

        currentRelease[field].error = null;
        currentRelease[field].loading = false;
        if (field === ReleaseFieldType.SongTitle) {
          currentRelease[field].data = updatedRelease.title;
          /* TODO: Remove this code in STR-3466
             Reason: This logic is temporary until song title and album get separated on the BE 
           */
          const currentSongData = currentRelease.songs[0];
          currentSongData.title = updatedRelease.title ?? '';
          const updatedSongData = updatedRelease.songs[0];

          if (hasError(currentRelease)) {
            currentSongData.status = ReleaseSongStatus.ERROR;
          } else {
            currentSongData.status = updatedSongData.status;
          }
        }
        if (field === ReleaseFieldType.Genre) {
          currentRelease[field].data = updatedRelease.genre;
        }
        if (field === ReleaseFieldType.ArtistName) {
          currentRelease[field].data = updatedRelease.artistName;
        }
        if (field === ReleaseFieldType.CoverArt) {
          currentRelease[field].data = updatedRelease.coverArtPath;
        }
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateReleaseSong.matchPending,
      (
        state,
        {
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { releaseId, field } = originalArgs;

        const currentRelease = state.releases[releaseId];
        if (!currentRelease.songs) {
          return;
        }

        const { metadata } = currentRelease.songs[0];
        metadata[field].loading = true;
        metadata[field].error = null;
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateReleaseSong.matchRejected,
      (state, action) => {
        const {
          payload,
          meta: {
            arg: { originalArgs },
          },
        } = action;
        const { releaseId, field } = originalArgs;

        const currentRelease = state.releases[releaseId];
        if (!currentRelease.songs || !payload) {
          return;
        }

        const currentReleaseSong = currentRelease.songs[0];
        currentReleaseSong.status = ReleaseSongStatus.ERROR;

        const { metadata } = currentReleaseSong;
        metadata[field].loading = false;
        metadata[field].error = payload as SerializedError;
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.updateReleaseSong.matchFulfilled,
      (
        state,
        {
          payload: updatedRelease,
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { releaseId, field } = originalArgs;
        const currentRelease = state.releases[releaseId];
        if (!currentRelease.songs) {
          return;
        }

        const currentSongData = currentRelease.songs[0];
        const { metadata: currentSongMetadata } = currentSongData;

        currentSongMetadata[field].error = null;
        currentSongMetadata[field].loading = false;

        const updatedSongData = updatedRelease.songs[0];
        if (hasError(currentRelease)) {
          currentSongData.status = ReleaseSongStatus.ERROR;
        } else {
          currentSongData.status = updatedSongData.status;
        }

        const { metadata: updatedSongMetadata } = updatedSongData;
        if (!updatedSongMetadata) {
          return;
        }

        if (field === ReleaseSongFieldType.ComposerName) {
          currentSongMetadata[field].data =
            updatedSongMetadata.composer ?? null;
        }
        if (field === ReleaseSongFieldType.HasVocals) {
          currentSongMetadata[field].data =
            updatedSongMetadata.hasVocals ?? null;
        }
        if (field === ReleaseSongFieldType.VocalsContent) {
          currentSongMetadata[field].data =
            updatedSongMetadata.vocalsContent ?? null;
        }
        if (field === ReleaseSongFieldType.VocalistName) {
          currentSongMetadata[field].data =
            updatedSongMetadata.vocalist ?? null;
        }
        if (field === ReleaseSongFieldType.Language) {
          currentSongMetadata[field].data =
            updatedSongMetadata.language ?? null;
        }
        if (field === ReleaseSongFieldType.AdditionalArtists) {
          currentSongMetadata[field].data =
            updatedSongMetadata.additionalArtists ?? null;
        }
        if (field === ReleaseSongFieldType.SongRemixers) {
          currentSongMetadata[field].data =
            updatedSongMetadata.remixers ?? null;
        }
        if (field === ReleaseSongFieldType.SongProducers) {
          currentSongMetadata[field].data =
            updatedSongMetadata.producers ?? null;
        }
      }
    );
    builder.addMatcher(
      distributionApi.endpoints.submitRelease.matchFulfilled,
      (state, { payload: release }) => {
        state.releases[release.id] = createReleaseState(release);
      }
    );
    builder.addMatcher(
      (action): action is { payload: ReleaseList } =>
        distributionApi.endpoints.getAllReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getDraftReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getPendingReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getReleasedReleases.matchFulfilled(action),
      (state, { payload: items }) => {
        items.releases.forEach((release) => {
          state.releases[release.id] = createReleaseState(release);
        });
      }
    );
  },
});

export const { actions, reducer } = distributionSlice;
